import { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import FuelVerification from './FuelVerification';
import { FleetTicks, FleetBadge, fleetParam } from '../../components/fuel/FleetTicks';
import { FleetSplitChart } from '../../components/fuel/FleetSplitChart';
import { FleetClassPanel } from '../../components/fuel/FleetClassPanel';
import { Fuel, Plus, Pencil, Trash2, Search, Car, Download, AlertTriangle, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple horizontal bar for the summary chart
function BarChart({ data, maxValue, formatCurrency }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="space-y-2">
      {data.map((item, idx) => {
        const pct = maxValue > 0 ? (item.total / maxValue) * 100 : 0;
        return (
          <div key={item.registration || idx} className="flex items-center gap-3" data-testid={`fuel-bar-${item.registration}`}>
            <div className="w-32 shrink-0 text-right">
              <p className="text-amber-700 font-mono text-sm font-bold truncate">{item.registration}</p>
            </div>
            <div className="flex-1 bg-slate-100 rounded-full h-7 overflow-hidden relative">
              <div
                className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-600 transition-all duration-500"
                style={{ width: `${Math.max(pct, 2)}%` }}
              />
              <span className="absolute inset-0 flex items-center px-3 text-xs font-bold text-slate-900">
                {formatCurrency(item.total)} ({item.count})
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function FuelTracker() {
  const [entries, setEntries] = useState([]);
  const [fleetSel, setFleetSel] = useState({ internal: true, external: true });
  const [pending, setPending] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [months, setMonths] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  // Filters
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedReg, setSelectedReg] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');

  // Dialogs
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showEditEntry, setShowEditEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  // Prioritise internal fleet in the plate picker. Toggle turns on the
  // external (owner-driver + one-off) plates when needed.
  const [showExternalFleet, setShowExternalFleet] = useState(false);
  // Pending Approvals queue — filter chip: all | internal | external
  const [pendingFilter, setPendingFilter] = useState('all');
  const [showEditVehicle, setShowEditVehicle] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);

  // Forms
  const [newEntry, setNewEntry] = useState({ date: new Date().toISOString().slice(0, 10), registration: '', driver_name: '', amount: '', km: '', fuel_station: 'BP Woodlands', payment_type: 'Account', reference: '', note: '' });
  const [newVehicle, setNewVehicle] = useState({ registration: '', driver_name: '', owner_name: '' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = me?.role === 'admin';
  const isClerk = me?.role === 'clerk';
  const formatCurrency = (amount) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount || 0);
  const formatMonth = (m) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    const names = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${names[parseInt(mo)] || mo} ${y}`;
  };

  // Set of known plates (uppercase) — used to highlight unknown plate during clerk capture
  const knownPlates = useMemo(() => new Set(vehicles.map(v => (v.registration || '').toUpperCase())), [vehicles]);
  const internalPlates = useMemo(() => new Set(vehicles.filter(v => v.fleet_class === 'internal').map(v => (v.registration || '').toUpperCase())), [vehicles]);
  const typedReg = (newEntry.registration || '').trim().toUpperCase();
  const isUnknownPlate = typedReg.length > 0 && !knownPlates.has(typedReg);
  const isTrustedInternalPlate = typedReg.length > 0 && internalPlates.has(typedReg);

  // ---- FETCH ----
  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (selectedMonth && selectedMonth !== 'all' && selectedMonth !== 'ytd') params.month = selectedMonth;
      if (selectedReg) params.registration = selectedReg;
      if (fleetParam(fleetSel)) params.fleet = fleetParam(fleetSel);

      const summaryParams = {};
      if (selectedMonth && selectedMonth !== 'all' && selectedMonth !== 'ytd') summaryParams.month = selectedMonth;
      if (fleetParam(fleetSel)) summaryParams.fleet = fleetParam(fleetSel);

      const [entryRes, vehicleRes, monthRes, summaryRes, pendingRes, meRes] = await Promise.all([
        axios.get(`${API_URL}/api/fuel-tracker/entries`, { headers, params }),
        axios.get(`${API_URL}/api/fuel-tracker/vehicles`, { headers }),
        axios.get(`${API_URL}/api/fuel-tracker/months`, { headers }),
        axios.get(`${API_URL}/api/fuel-tracker/summary`, { headers, params: summaryParams }),
        axios.get(`${API_URL}/api/fuel-tracker/pending`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/auth/me`, { headers }).catch(() => ({ data: null })),
      ]);
      setEntries(entryRes.data);
      setVehicles(vehicleRes.data);
      setMonths(monthRes.data);
      setSummary(summaryRes.data);
      setPending(pendingRes.data || []);
      if (meRes.data) setMe(meRes.data);
    } catch { toast.error('Failed to load fuel data'); }
    finally { setLoading(false); }
  }, [selectedMonth, selectedReg, fleetSel]);

  useEffect(() => { fetchData(); }, [selectedMonth, selectedReg, fleetSel]);
  useEffect(() => { if (months.length > 0 && !selectedMonth) setSelectedMonth(months[0]); }, [months]);

  // ---- ENTRY HANDLERS ----
  const handleAddEntry = async () => {
    if (!newEntry.registration.trim()) { toast.error('Vehicle registration required'); return; }
    if (!newEntry.amount) { toast.error('Amount required'); return; }
    if (!newEntry.reference.trim()) { toast.error('Reference (invoice/receipt no.) required'); return; }
    // Clerk + unknown plate → confirm "send for admin approval"
    if (isClerk && isUnknownPlate) {
      const ok = window.confirm(
        `Plate "${typedReg}" is not on the master list.\n\n` +
        `This entry will be sent for ADMIN APPROVAL — it will NOT show on the live ledger until approved.\n\n` +
        `Continue?`
      );
      if (!ok) return;
    }
    try {
      const payload = { ...newEntry, registration: newEntry.registration.trim().toUpperCase() };
      const res = await axios.post(`${API_URL}/api/fuel-tracker/entries`, payload, { headers });
      if (res.data?.status === 'pending_approval') {
        toast.success('Submitted for admin approval — admin will review and add the plate.');
      } else {
        toast.success('Fuel entry added!');
      }
      setNewEntry({ date: new Date().toISOString().slice(0, 10), registration: '', driver_name: '', amount: '', km: '', fuel_station: 'BP Woodlands', payment_type: 'Account', reference: '', note: '' });
      setShowAddEntry(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleApprovePending = async (id) => {
    try {
      await axios.post(`${API_URL}/api/fuel-tracker/pending/${id}/approve`, {}, { headers });
      toast.success('Approved & plate registered');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRejectPending = async (id) => {
    if (!window.confirm('Reject and delete this pending fuel entry?')) return;
    try {
      await axios.post(`${API_URL}/api/fuel-tracker/pending/${id}/reject`, {}, { headers });
      toast.success('Rejected & removed');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleEditEntry = async () => {
    if (!editingEntry) return;
    if (!(editingEntry.reference || '').trim()) { toast.error('Reference is required'); return; }
    try {
      await axios.put(`${API_URL}/api/fuel-tracker/entries/${editingEntry.id}`, {
        date: editingEntry.date, registration: editingEntry.registration, driver_name: editingEntry.driver_name,
        amount: parseFloat(editingEntry.amount) || 0, fuel_station: editingEntry.fuel_station, payment_type: editingEntry.payment_type || 'Account', reference: editingEntry.reference, note: editingEntry.note
      }, { headers });
      toast.success('Updated!'); setShowEditEntry(false); setEditingEntry(null); fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Delete this fuel entry?')) return;
    try { await axios.delete(`${API_URL}/api/fuel-tracker/entries/${id}`, { headers }); toast.success('Deleted!'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  // ---- VEHICLE HANDLERS ----
  const handleAddVehicle = async () => {
    if (!newVehicle.registration) { toast.error('Registration required'); return; }
    try {
      await axios.post(`${API_URL}/api/fuel-tracker/vehicles`, newVehicle, { headers });
      toast.success('Vehicle added!');
      setNewVehicle({ registration: '', driver_name: '', owner_name: '' });
      setShowAddVehicle(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleEditVehicle = async () => {
    if (!editingVehicle) return;
    try {
      await axios.post(`${API_URL}/api/fuel-tracker/vehicles/assign`, {
        registration: editingVehicle.registration,
        driver_name: editingVehicle.driver_name,
        owner_name: editingVehicle.owner_name
      }, { headers });
      toast.success('Driver assigned!'); setShowEditVehicle(false); setEditingVehicle(null); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteVehicle = async (id) => {
    if (!window.confirm('Remove this vehicle?')) return;
    try { await axios.delete(`${API_URL}/api/fuel-tracker/vehicles/${id}`, { headers }); toast.success('Removed!'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const handleExportXlsx = async () => {
    try {
      const params = {};
      if (selectedMonth && selectedMonth !== 'all' && selectedMonth !== 'ytd') params.month = selectedMonth;
      if (selectedReg) params.registration = selectedReg;
      const res = await axios.get(`${API_URL}/api/fuel-tracker/export-xlsx`, {
        headers,
        params,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      a.download = m ? m[1] : 'jsm-fuel-tracker.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Fuel report exported');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
  };

  // ---- DERIVED ----
  const filteredEntries = useMemo(() => {
    let result = [...entries];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(e => (e.registration || '').toLowerCase().includes(q) || (e.driver_name || '').toLowerCase().includes(q) || (e.note || '').toLowerCase().includes(q));
    }
    return result.sort((a, b) => a.date < b.date ? -1 : a.date > b.date ? 1 : 0);
  }, [entries, searchQuery]);

  const filteredVehiclesList = useMemo(() => {
    if (!vehicleSearch) return vehicles;
    const q = vehicleSearch.toLowerCase();
    return vehicles.filter(v => (v.registration || '').toLowerCase().includes(q) || (v.driver_name || '').toLowerCase().includes(q) || (v.owner_name || '').toLowerCase().includes(q));
  }, [vehicles, vehicleSearch]);

  const filteredTotal = filteredEntries.reduce((s, e) => s + (e.amount || 0), 0);
  const chartMax = summary?.by_vehicle?.[0]?.total || 0;

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64 text-slate-600">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Fuel className="h-6 w-6 text-amber-700" /> Fuel Tracker
            </h1>
            <p className="text-slate-600 text-sm">BP Woodlands</p>
          </div>
          {/* Month selector in header */}
          <div className="flex flex-wrap items-center gap-3">
            <FleetTicks value={fleetSel} onChange={setFleetSel} />
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900 w-48" data-testid="month-filter">
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="all">All Months</SelectItem>
                <SelectItem value="ytd">Year to Date</SelectItem>
                {months.map(m => <SelectItem key={m} value={m}>{formatMonth(m)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Totals strip */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-gradient-to-r from-amber-500 to-amber-600 border-0 text-white">
            <CardContent className="p-3 flex items-center justify-between">
              <span className="text-sm opacity-80">Total Fuel</span>
              <span className="text-xl font-bold" data-testid="fuel-grand-total">{formatCurrency(summary?.grand_total)}</span>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-r from-blue-500 to-blue-600 border-0 text-white">
            <CardContent className="p-3 flex items-center justify-between">
              <span className="text-sm opacity-80">Entries</span>
              <span className="text-xl font-bold" data-testid="fuel-entry-count">{summary?.entry_count || 0}</span>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 border-0 text-white">
            <CardContent className="p-3 flex items-center justify-between">
              <span className="text-sm opacity-80">Vehicles</span>
              <span className="text-xl font-bold">{summary?.by_vehicle?.length || 0}</span>
            </CardContent>
          </Card>
        </div>

        {/* TABS */}
        <Tabs defaultValue="entries">
          <TabsList className="bg-slate-100 mb-4">
            <TabsTrigger value="entries" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-slate-700" data-testid="entries-tab">Monthly Entries</TabsTrigger>
            <TabsTrigger value="summary" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white text-slate-700" data-testid="summary-tab">Summary & Graph</TabsTrigger>
            <TabsTrigger value="vehicles" className="data-[state=active]:bg-cyan-600 text-white" data-testid="vehicles-tab">Vehicles & Drivers</TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white text-slate-700 relative" data-testid="pending-tab">
              Pending Approvals
              {pending.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center text-[10px] font-bold bg-rose-600 text-white rounded-full px-1.5 min-w-[18px] h-[18px]" data-testid="pending-count-badge">{pending.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="fleet-class" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-700" data-testid="fleet-class-tab">Internal / External</TabsTrigger>
            <TabsTrigger value="verify" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-700" data-testid="verify-tab">
              Month-End Verify
            </TabsTrigger>
          </TabsList>

          {/* ====== ENTRIES TAB ====== */}
          <TabsContent value="entries">
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div className="min-w-[200px]">
                <Label className="text-slate-600 text-xs">Filter by Vehicle</Label>
                <Select value={selectedReg} onValueChange={(v) => setSelectedReg(v === 'all' ? '' : v)}>
                  <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="vehicle-filter">
                    <SelectValue placeholder="All Vehicles" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 max-h-60">
                    <SelectItem value="all">All Vehicles</SelectItem>
                    {vehicles.map(v => <SelectItem key={v.registration} value={v.registration}>{v.registration}{v.driver_name ? ` - ${v.driver_name}` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[180px]">
                <Label className="text-slate-600 text-xs">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
                  <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search..." className="bg-white border-slate-300 text-slate-900 pl-10" data-testid="fuel-search-input" />
                </div>
              </div>
              <Dialog open={showAddEntry} onOpenChange={setShowAddEntry}>
                <DialogTrigger asChild>
                  <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-fuel-entry-btn"><Plus className="h-4 w-4 mr-1" /> Add Entry</Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md">
                  <DialogHeader><DialogTitle className="flex items-center gap-2"><Fuel className="h-5 w-5 text-amber-700" /> NEW FUEL ENTRY</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div><Label>Date *</Label><Input type="date" value={newEntry.date} onChange={e => setNewEntry({...newEntry, date: e.target.value})} className="bg-white border-slate-200" data-testid="fuel-date-input" /></div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label>Vehicle Registration *</Label>
                        <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer" data-testid="fuel-fleet-toggle-label">
                          <input
                            type="checkbox"
                            checked={showExternalFleet}
                            onChange={(e) => setShowExternalFleet(e.target.checked)}
                            className="h-3 w-3 accent-amber-600"
                            data-testid="fuel-fleet-toggle"
                          />
                          Include external fleet
                        </label>
                      </div>
                      <Input
                        list="known-plates-datalist"
                        value={newEntry.registration}
                        onChange={e => {
                          const reg = e.target.value.toUpperCase();
                          const v = vehicles.find(x => x.registration === reg);
                          setNewEntry(prev => ({ ...prev, registration: reg, driver_name: v?.driver_name || prev.driver_name }));
                        }}
                        placeholder={showExternalFleet ? 'Type or pick — internal + external' : 'Type or pick — internal fleet only'}
                        className="bg-white border-slate-200 uppercase font-mono"
                        data-testid="fuel-reg-input"
                      />
                      <datalist id="known-plates-datalist">
                        {vehicles
                          .filter(v => showExternalFleet || v.fleet_class === 'internal')
                          .map(v => (
                            <option key={v.registration} value={v.registration}>
                              {v.driver_name ? `${v.registration} — ${v.driver_name}` : v.registration}
                              {v.fleet_class === 'external' ? '  (external)' : ''}
                            </option>
                          ))}
                      </datalist>
                      <p className="text-[10px] text-slate-500 mt-1">
                        {showExternalFleet
                          ? `Showing ${vehicles.length} plates (internal + external).`
                          : `Showing ${vehicles.filter(v => v.fleet_class === 'internal').length} internal fleet plates. Tick to include external.`}
                      </p>
                      {isTrustedInternalPlate && isClerk && (
                        <div className="mt-2 flex items-start gap-2 rounded p-2 text-xs bg-emerald-50 border border-emerald-300 text-emerald-900" data-testid="trusted-plate-info">
                          <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
                          <div>
                            <p className="font-semibold">Internal fleet plate — will auto-approve</p>
                            <p>This entry will land on the live ledger immediately (no admin queue).</p>
                          </div>
                        </div>
                      )}
                      {isUnknownPlate && (
                        <div className={`mt-2 flex items-start gap-2 rounded p-2 text-xs ${isClerk ? 'bg-amber-50 border border-amber-300 text-amber-900' : 'bg-blue-50 border border-blue-300 text-blue-900'}`} data-testid="unknown-plate-warning">
                          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                          <div>
                            <p className="font-semibold">Plate not on master list</p>
                            {isClerk
                              ? <p>This entry will be submitted for <b>admin approval</b> before it counts.</p>
                              : <p>It will be added to the master list automatically when you save.</p>}
                          </div>
                        </div>
                      )}
                    </div>
                    <div><Label>Driver</Label><Input value={newEntry.driver_name} onChange={e => setNewEntry({...newEntry, driver_name: e.target.value})} placeholder="Auto-filled" className="bg-white border-slate-200" data-testid="fuel-driver-input" /></div>
                    <div><Label>Amount (R) *</Label><Input type="number" step="0.01" value={newEntry.amount} onChange={e => setNewEntry({...newEntry, amount: e.target.value})} placeholder="0.00" className="bg-white border-slate-200" data-testid="fuel-amount-input" /></div>
                    <div>
                      <Label>Odometer / KM <span className="text-xs text-slate-500">(optional — flows to Fleet Maintenance)</span></Label>
                      <Input type="number" min="0" value={newEntry.km} onChange={e => setNewEntry({...newEntry, km: e.target.value})} placeholder="e.g. 95000" className="bg-white border-slate-200" data-testid="fuel-km-input" />
                    </div>
                    <div>
                      <Label>Reference * <span className="text-xs text-slate-500">(invoice / receipt no.)</span></Label>
                      <Input value={newEntry.reference} onChange={e => setNewEntry({...newEntry, reference: e.target.value})} placeholder="e.g. INV-12345" className="bg-white border-slate-200" data-testid="fuel-reference-input" />
                    </div>
                    <div>
                      <Label>Payment Type *</Label>
                      <Select value={newEntry.payment_type} onValueChange={val => setNewEntry({...newEntry, payment_type: val})}>
                        <SelectTrigger className="bg-white border-slate-200" data-testid="fuel-payment-type"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-slate-200">
                          <SelectItem value="Account">Account</SelectItem>
                          <SelectItem value="Cash">Cash</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Station</Label><Input value={newEntry.fuel_station} onChange={e => setNewEntry({...newEntry, fuel_station: e.target.value})} className="bg-white border-slate-200" /></div>
                      <div><Label>Note</Label><Input value={newEntry.note} onChange={e => setNewEntry({...newEntry, note: e.target.value})} placeholder="Optional" className="bg-white border-slate-200" /></div>
                    </div>
                    <Button onClick={handleAddEntry} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="save-fuel-entry-btn">
                      {isClerk && isUnknownPlate ? 'SUBMIT FOR ADMIN APPROVAL' : 'SAVE ENTRY'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              <Button onClick={handleExportXlsx} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="fuel-export-xlsx-btn">
                <Download className="h-4 w-4 mr-1" /> Export XLSX
              </Button>
            </div>

            {/* Entries Table */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-0">
                {filteredEntries.length === 0 ? (
                  <p className="text-slate-600 text-center py-8">No entries found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-200">
                          <TableHead className="text-slate-600">Date</TableHead>
                          <TableHead className="text-slate-600">Registration</TableHead>
                          <TableHead className="text-slate-600">Driver</TableHead>
                          <TableHead className="text-slate-600 text-right">Amount</TableHead>
                          <TableHead className="text-slate-600">Type</TableHead>
                          <TableHead className="text-slate-600">Reference</TableHead>
                          <TableHead className="text-slate-600">Note</TableHead>
                          <TableHead className="text-slate-600 text-right w-20">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredEntries.map(e => (
                          <TableRow key={e.id} className="border-slate-200">
                            <TableCell className="text-slate-700 text-sm">{new Date(e.date).toLocaleDateString('en-ZA')}</TableCell>
                            <TableCell className="text-amber-700 font-mono text-sm font-medium">{e.registration} <FleetBadge fc={e.fleet_class} testId={`entry-fleet-${e.id}`} /></TableCell>
                            <TableCell className="text-slate-900 text-sm">{e.driver_name || '-'}</TableCell>
                            <TableCell className="text-emerald-700 text-right font-bold text-sm">{formatCurrency(e.amount)}</TableCell>
                            <TableCell><span className={`text-xs px-2 py-0.5 rounded ${e.payment_type === 'Cash' ? 'bg-green-600/20 text-green-400' : 'bg-blue-100 text-blue-700'}`}>{e.payment_type || 'Account'}</span></TableCell>
                            <TableCell className="text-slate-700 text-xs font-mono">{e.reference || '-'}</TableCell>
                            <TableCell className="text-slate-600 text-sm max-w-[180px]">
                              {e.missing_slip && <div className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 w-fit" data-testid={`fuel-missing-slip-${e.id}`}>Missing slip – added at month end · {formatCurrency(e.slip_amount)}</div>}
                              <div className="truncate">{e.note || (e.missing_slip ? '' : '-')}</div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-6 w-6 p-0" data-testid={`edit-fuel-${e.id}`} onClick={() => { setEditingEntry({...e}); setShowEditEntry(true); }}><Pencil className="h-3 w-3" /></Button>
                                <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white h-6 w-6 p-0" data-testid={`delete-fuel-${e.id}`} onClick={() => handleDeleteEntry(e.id)}><Trash2 className="h-3 w-3" /></Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="border-slate-200 bg-slate-100">
                          <TableCell colSpan={4} className="text-slate-900 font-bold text-sm">TOTAL ({filteredEntries.length} entries)</TableCell>
                          <TableCell className="text-amber-700 text-right font-bold">{formatCurrency(filteredTotal)}</TableCell>
                          <TableCell colSpan={4} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ====== SUMMARY TAB ====== */}
          <TabsContent value="summary">
            {summary?.by_vehicle?.length > 0 ? (
              <div className="space-y-5">
                {/* Internal vs External split */}
                {summary.by_fleet_class && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="fuel-fleet-split">
                    {['internal', 'external'].map((fc) => {
                      const b = summary.by_fleet_class[fc] || { total: 0, count: 0, percent: 0 };
                      const isInternal = fc === 'internal';
                      return (
                        <Card
                          key={fc}
                          className={`shadow-sm ${isInternal ? 'border-emerald-300 bg-emerald-50' : 'border-indigo-300 bg-indigo-50'}`}
                          data-testid={`fleet-card-${fc}`}
                        >
                          <CardContent className="py-3 px-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className={`text-[10px] uppercase tracking-wider font-semibold ${isInternal ? 'text-emerald-700' : 'text-indigo-700'}`}>
                                  {isInternal ? 'Internal Fleet' : 'External Fleet'}
                                </div>
                                <div className={`text-2xl font-extrabold ${isInternal ? 'text-emerald-900' : 'text-indigo-900'}`}>
                                  {formatCurrency(b.total)}
                                </div>
                                <div className={`text-xs ${isInternal ? 'text-emerald-700/80' : 'text-indigo-700/80'}`}>
                                  {b.count} {b.count === 1 ? 'entry' : 'entries'} · {b.percent}% of month
                                </div>
                              </div>
                              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isInternal ? 'bg-emerald-600' : 'bg-indigo-600'} text-white text-xl font-bold`}>
                                {b.percent}%
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}

                <FleetSplitChart headers={headers} fleetSel={fleetSel} refreshKey={summary} />

                {/* Bar Chart */}
                <Card className="bg-white border-slate-200 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-slate-900 text-base">Fuel Cost Per Vehicle — {selectedMonth === 'all' ? 'All Time' : selectedMonth === 'ytd' ? 'Year to Date' : formatMonth(selectedMonth)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BarChart data={summary.by_vehicle} maxValue={chartMax} formatCurrency={formatCurrency} />
                    <div className="mt-4 pt-3 border-t border-slate-200 flex justify-between items-center">
                      <span className="text-slate-900 font-bold">Grand Total</span>
                      <span className="text-amber-700 font-bold text-xl">{formatCurrency(summary.grand_total)}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Detail Table */}
                <Card className="bg-white border-slate-200 shadow-sm">
                  <CardHeader className="pb-2"><CardTitle className="text-slate-900 text-base">Breakdown</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-200">
                          <TableHead className="text-slate-600">Registration</TableHead>
                          <TableHead className="text-slate-600">Driver</TableHead>
                          <TableHead className="text-slate-600 text-right">Entries</TableHead>
                          <TableHead className="text-slate-600 text-right">Total</TableHead>
                          <TableHead className="text-slate-600 text-right">%</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {summary.by_vehicle.map((v, i) => (
                          <TableRow key={i} className="border-slate-200 cursor-pointer hover:bg-slate-100" onClick={() => { setSelectedReg(v.registration === selectedReg ? '' : v.registration); }} data-testid={`vehicle-summary-${v.registration}`}>
                            <TableCell className="text-amber-700 font-mono font-medium text-sm">{v.registration} <FleetBadge fc={v.fleet_class} testId={`summary-fleet-${v.registration}`} /></TableCell>
                            <TableCell className="text-slate-900 text-sm">{v.driver_name || '-'}</TableCell>
                            <TableCell className="text-slate-900 text-right text-sm">{v.count}</TableCell>
                            <TableCell className="text-emerald-700 text-right font-bold text-sm">
                              {formatCurrency(v.total)}
                              {v.missing_slip_count > 0 && <div className="text-[10px] font-semibold text-rose-600" data-testid={`vehicle-missing-${v.registration}`}>+{formatCurrency(v.missing_slip_total)} missing slip{v.missing_slip_count > 1 ? 's' : ''}</div>}
                            </TableCell>
                            <TableCell className="text-slate-600 text-right text-sm">{summary.grand_total > 0 ? ((v.total / summary.grand_total) * 100).toFixed(1) : 0}%</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="border-slate-200 bg-slate-100">
                          <TableCell colSpan={3} className="text-slate-900 font-bold text-sm">GRAND TOTAL</TableCell>
                          <TableCell className="text-amber-700 text-right font-bold">{formatCurrency(summary.grand_total)}</TableCell>
                          <TableCell className="text-slate-900 text-right font-bold text-sm">100%</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            ) : <p className="text-slate-600 text-center py-12">No fuel data for this period.</p>}
          </TabsContent>

          {/* ====== VEHICLES TAB ====== */}
          <TabsContent value="vehicles">
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
                  <Input value={vehicleSearch} onChange={e => setVehicleSearch(e.target.value)} placeholder="Search vehicles..." className="bg-white border-slate-300 text-slate-900 pl-10" data-testid="vehicle-search-input" />
                </div>
              </div>
              <Dialog open={showAddVehicle} onOpenChange={setShowAddVehicle}>
                <DialogTrigger asChild>
                  <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="add-vehicle-btn"><Plus className="h-4 w-4 mr-1" /> Add Vehicle</Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md">
                  <DialogHeader><DialogTitle className="flex items-center gap-2"><Car className="h-5 w-5 text-cyan-700" /> ADD VEHICLE</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div><Label>Registration *</Label><Input value={newVehicle.registration} onChange={e => setNewVehicle({...newVehicle, registration: e.target.value.toUpperCase()})} placeholder="e.g., CA 123-456" className="bg-white border-slate-200 uppercase" data-testid="vehicle-reg-input" /></div>
                    <div><Label>Driver Name</Label><Input value={newVehicle.driver_name} onChange={e => setNewVehicle({...newVehicle, driver_name: e.target.value})} placeholder="e.g., John Smith" className="bg-white border-slate-200" data-testid="vehicle-driver-input" /></div>
                    <div><Label>Owner Name</Label><Input value={newVehicle.owner_name} onChange={e => setNewVehicle({...newVehicle, owner_name: e.target.value})} placeholder="e.g., JSM Fleet" className="bg-white border-slate-200" data-testid="vehicle-owner-input" /></div>
                    <Button onClick={handleAddVehicle} className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="save-vehicle-btn">ADD VEHICLE</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200">
                      <TableHead className="text-slate-600">Registration</TableHead>
                      <TableHead className="text-slate-600">Driver Name</TableHead>
                      <TableHead className="text-slate-600">Owner</TableHead>
                      <TableHead className="text-slate-600">Source</TableHead>
                      <TableHead className="text-slate-600 text-right w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredVehiclesList.map(v => (
                      <TableRow key={v.registration} className="border-slate-200">
                        <TableCell className="text-amber-700 font-mono font-medium text-sm">{v.registration}</TableCell>
                        <TableCell className="text-slate-900 text-sm">{v.driver_name || <span className="text-slate-500 italic">Not assigned</span>}</TableCell>
                        <TableCell className="text-slate-600 text-sm">{v.owner_name || '-'}</TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-0.5 rounded ${v.source === 'fuel_vehicles' ? 'bg-cyan-100 text-cyan-700' : v.source === 'drivers' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-600/20 text-slate-600'}`}>
                            {v.source === 'fuel_vehicles' ? 'Fuel Tracker' : v.source === 'drivers' ? 'Courier Portal' : 'Fuel Entries'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-6 px-2 text-xs" data-testid={`edit-vehicle-${v.registration}`} onClick={() => { setEditingVehicle({...v}); setShowEditVehicle(true); }}><Pencil className="h-3 w-3 mr-1" />Assign</Button>
                            {v.source === 'fuel_vehicles' && v.id && (
                              <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white h-6 w-6 p-0" data-testid={`delete-vehicle-${v.registration}`} onClick={() => handleDeleteVehicle(v.id)}><Trash2 className="h-3 w-3" /></Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ====== PENDING APPROVALS TAB ====== */}
          <TabsContent value="pending">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <CardTitle className="text-slate-900 text-base flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-rose-600" />
                      Awaiting Admin Approval ({pending.length})
                    </CardTitle>
                    <p className="text-xs text-slate-600">
                      Clerks who capture fuel for an <b>unknown number plate</b> land here.
                      {isAdmin ? ' Approve to register the plate on the master list and add the entry to the live ledger.' : ' Only admins can approve.'}
                    </p>
                  </div>
                  {/* Fleet-class filter chips */}
                  <div className="inline-flex gap-1 rounded border border-slate-200 p-0.5 bg-slate-50" data-testid="pending-fleet-filter">
                    {[
                      { v: 'all', label: 'All' },
                      { v: 'internal', label: `Internal (${pending.filter((p) => (p.fleet_class || 'external') === 'internal').length})` },
                      { v: 'external', label: `External (${pending.filter((p) => (p.fleet_class || 'external') === 'external').length})` },
                    ].map((opt) => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setPendingFilter(opt.v)}
                        className={`px-2 py-0.5 text-xs rounded transition ${pendingFilter === opt.v ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-200'}`}
                        data-testid={`pending-filter-${opt.v}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {(() => {
                  const filtered = pending.filter((p) => pendingFilter === 'all' || (p.fleet_class || 'external') === pendingFilter);
                  if (filtered.length === 0) {
                    return (
                      <p className="text-slate-600 text-center py-8" data-testid="no-pending">
                        {pending.length === 0
                          ? 'No pending entries. All clerk submissions are auto-approved when the plate is known.'
                          : `No ${pendingFilter} pending entries.`}
                      </p>
                    );
                  }
                  return (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-200">
                          <TableHead className="text-slate-600">Submitted</TableHead>
                          <TableHead className="text-slate-600">By</TableHead>
                          <TableHead className="text-slate-600">Date</TableHead>
                          <TableHead className="text-slate-600">Plate</TableHead>
                          <TableHead className="text-slate-600">Fleet</TableHead>
                          <TableHead className="text-slate-600">Driver</TableHead>
                          <TableHead className="text-slate-600 text-right">Amount</TableHead>
                          <TableHead className="text-slate-600">Reference</TableHead>
                          <TableHead className="text-slate-600">Note</TableHead>
                          <TableHead className="text-slate-600 text-right w-44">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map(p => {
                          const fc = p.fleet_class || 'external';
                          return (
                          <TableRow key={p.id} className="border-slate-200 bg-rose-50/50" data-testid={`pending-row-${p.id}`}>
                            <TableCell className="text-slate-700 text-xs">{p.created_at ? new Date(p.created_at).toLocaleString('en-ZA') : '-'}</TableCell>
                            <TableCell className="text-slate-900 text-sm">{p.added_by || '-'}</TableCell>
                            <TableCell className="text-slate-700 text-sm">{p.date}</TableCell>
                            <TableCell className="text-amber-700 font-mono text-sm font-bold">{p.registration}</TableCell>
                            <TableCell>
                              <span
                                className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${fc === 'internal' ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-indigo-50 text-indigo-800 border-indigo-300'}`}
                                data-testid={`pending-fleet-badge-${p.id}`}
                              >
                                {fc}
                              </span>
                            </TableCell>
                            <TableCell className="text-slate-700 text-sm">{p.driver_name || '-'}</TableCell>
                            <TableCell className="text-emerald-700 text-right font-bold text-sm">{formatCurrency(p.amount)}</TableCell>
                            <TableCell className="text-slate-700 text-xs font-mono">{p.reference || '-'}</TableCell>
                            <TableCell className="text-slate-600 text-xs max-w-[140px] truncate">{p.note || '-'}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-2 text-xs" disabled={!isAdmin} data-testid={`approve-${p.id}`} onClick={() => handleApprovePending(p.id)}>
                                  <ShieldCheck className="h-3 w-3 mr-1" /> Approve
                                </Button>
                                <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white h-7 px-2 text-xs" disabled={!isAdmin} data-testid={`reject-${p.id}`} onClick={() => handleRejectPending(p.id)}>
                                  <X className="h-3 w-3 mr-1" /> Reject
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="fleet-class">
            <FleetClassPanel headers={headers} onChanged={fetchData} />
          </TabsContent>

          {/* ====== MONTH-END VERIFY TAB ====== */}
          <TabsContent value="verify">
            {(!selectedMonth || selectedMonth === 'all' || selectedMonth === 'ytd') ? (
              <div className="bg-amber-50 border border-amber-200 rounded p-4 text-sm text-amber-900">
                Pick a specific month in the <b>Filter by Month</b> selector at the top to run the month-end verification.
              </div>
            ) : (
              <FuelVerification month={selectedMonth} headers={headers} onEntriesChanged={fetchData} fleet={fleetParam(fleetSel)} />
            )}
          </TabsContent>
        </Tabs>

        {/* ===== EDIT DIALOGS ===== */}
        <Dialog open={showEditEntry} onOpenChange={setShowEditEntry}>
          <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5" /> EDIT ENTRY</DialogTitle></DialogHeader>
            {editingEntry && (
              <div className="space-y-4">
                <div><Label>Date</Label><Input type="date" value={editingEntry.date} onChange={e => setEditingEntry({...editingEntry, date: e.target.value})} className="bg-white border-slate-200" /></div>
                <div>
                  <Label>Vehicle Registration</Label>
                  <Input
                    list="known-plates-datalist"
                    value={editingEntry.registration || ''}
                    onChange={e => {
                      const reg = e.target.value.toUpperCase();
                      const v = vehicles.find(x => x.registration === reg);
                      setEditingEntry({...editingEntry, registration: reg, driver_name: v?.driver_name || editingEntry.driver_name});
                    }}
                    className="bg-white border-slate-200 uppercase font-mono"
                  />
                </div>
                <div><Label>Driver</Label><Input value={editingEntry.driver_name || ''} onChange={e => setEditingEntry({...editingEntry, driver_name: e.target.value})} className="bg-white border-slate-200" /></div>
                <div><Label>Amount (R)</Label><Input type="number" step="0.01" value={editingEntry.amount} onChange={e => setEditingEntry({...editingEntry, amount: e.target.value})} className="bg-white border-slate-200" /></div>
                <div>
                  <Label>Reference * <span className="text-xs text-slate-500">(invoice / receipt)</span></Label>
                  <Input value={editingEntry.reference || ''} onChange={e => setEditingEntry({...editingEntry, reference: e.target.value})} placeholder="e.g. INV-12345" className="bg-white border-slate-200" data-testid="edit-fuel-reference-input" />
                </div>
                <div>
                  <Label>Payment Type</Label>
                  <Select value={editingEntry.payment_type || 'Account'} onValueChange={val => setEditingEntry({...editingEntry, payment_type: val})}>
                    <SelectTrigger className="bg-white border-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="Account">Account</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Station</Label><Input value={editingEntry.fuel_station || ''} onChange={e => setEditingEntry({...editingEntry, fuel_station: e.target.value})} className="bg-white border-slate-200" /></div>
                  <div><Label>Note</Label><Input value={editingEntry.note || ''} onChange={e => setEditingEntry({...editingEntry, note: e.target.value})} className="bg-white border-slate-200" /></div>
                </div>
                <Button onClick={handleEditEntry} className="w-full bg-blue-600 hover:bg-blue-700">UPDATE</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showEditVehicle} onOpenChange={setShowEditVehicle}>
          <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Car className="h-5 w-5 text-cyan-700" /> ASSIGN DRIVER — {editingVehicle?.registration}</DialogTitle></DialogHeader>
            {editingVehicle && (
              <div className="space-y-4">
                <div className="bg-slate-100 p-3 rounded-lg">
                  <p className="text-sm text-slate-600">Vehicle Registration</p>
                  <p className="text-amber-700 font-mono font-bold text-lg">{editingVehicle.registration}</p>
                </div>

                {/* Driver Name: Link from system OR manual type */}
                <div>
                  <Label className="mb-1 block">Driver Name *</Label>
                  <div className="space-y-2">
                    <Select
                      value={editingVehicle.driver_name || ''}
                      onValueChange={val => setEditingVehicle({...editingVehicle, driver_name: val === '__clear__' ? '' : val})}
                    >
                      <SelectTrigger className="bg-white border-slate-200" data-testid="assign-driver-select">
                        <SelectValue placeholder="Pick from system drivers..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200 max-h-60">
                        <SelectItem value="__clear__">-- Clear selection --</SelectItem>
                        {[...new Set(vehicles.filter(v => v.driver_name).map(v => v.driver_name))].sort().map(name => (
                          <SelectItem key={name} value={name}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2">
                      <div className="h-px flex-1 bg-slate-600" />
                      <span className="text-slate-500 text-xs">OR type manually</span>
                      <div className="h-px flex-1 bg-slate-600" />
                    </div>
                    <Input
                      value={editingVehicle.driver_name || ''}
                      onChange={e => setEditingVehicle({...editingVehicle, driver_name: e.target.value})}
                      placeholder="Type driver name..."
                      className="bg-white border-slate-200"
                      data-testid="assign-driver-manual"
                    />
                  </div>
                </div>

                {/* Owner Name: Link from system OR manual type */}
                <div>
                  <Label className="mb-1 block">Owner Name</Label>
                  <div className="space-y-2">
                    <Select
                      value={editingVehicle.owner_name || ''}
                      onValueChange={val => setEditingVehicle({...editingVehicle, owner_name: val === '__clear__' ? '' : val})}
                    >
                      <SelectTrigger className="bg-white border-slate-200" data-testid="assign-owner-select">
                        <SelectValue placeholder="Pick from system owners..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200 max-h-60">
                        <SelectItem value="__clear__">-- Clear selection --</SelectItem>
                        {[...new Set(vehicles.filter(v => v.owner_name).map(v => v.owner_name))].sort().map(name => (
                          <SelectItem key={name} value={name}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2">
                      <div className="h-px flex-1 bg-slate-600" />
                      <span className="text-slate-500 text-xs">OR type manually</span>
                      <div className="h-px flex-1 bg-slate-600" />
                    </div>
                    <Input
                      value={editingVehicle.owner_name || ''}
                      onChange={e => setEditingVehicle({...editingVehicle, owner_name: e.target.value})}
                      placeholder="Type owner name..."
                      className="bg-white border-slate-200"
                      data-testid="assign-owner-manual"
                    />
                  </div>
                </div>

                <Button onClick={handleEditVehicle} className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="save-assign-btn">SAVE & LINK TO ALL ENTRIES</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
